/**
 * Newsletter opt-in modal — Substack-style, shown once to every
 * logged-in community member who hasn't subscribed or dismissed in
 * the last 7 days.
 *
 * All show / hide / subscribe / dismiss state lives in
 * newsletterPrompt.duck — this component is pure presentation.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Modal, PrimaryButton, InlineTextButton } from '../../components';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { notification } from '../../util/notification';
import {
  closeNewsletterPrompt,
  dismissNewsletterPrompt,
  subscribeToNewsletter,
} from '../../ducks/newsletterPrompt.duck';

import css from './NewsletterPromptModal.module.css';

const NewsletterPromptModal = ({ id }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isOpen, subscribeInProgress, subscribeError } = useSelector(
    state => state.newsletterPrompt || {}
  );

  // Closing via the chrome × — treat as "maybe later" so the
  // 7-day suppression kicks in.
  const handleClose = () => {
    dispatch(dismissNewsletterPrompt());
    dispatch(closeNewsletterPrompt());
  };

  // The thunk closes the modal itself on success — we only need to
  // surface the toast. On error the thunk leaves isOpen=true and
  // sets subscribeError, which we render inline below.
  const handleSubscribe = () => {
    dispatch(subscribeToNewsletter()).then(result => {
      if (result?.ok) {
        notification.success(
          intl.formatMessage({ id: 'NewsletterPromptModal.successToast' })
        );
      }
    });
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={(componentId, disable) =>
        dispatch(manageDisableScrolling(componentId, disable))
      }
      usePortal
    >
      <div className={css.body}>
        <h2 className={css.title}>
          <FormattedMessage id="NewsletterPromptModal.title" />
        </h2>
        <p className={css.copy}>
          <FormattedMessage id="NewsletterPromptModal.copy" />
        </p>
        {subscribeError ? (
          <p className={css.error}>
            <FormattedMessage id="NewsletterPromptModal.error" />
          </p>
        ) : null}
        <div className={css.actions}>
          <PrimaryButton
            className={css.subscribeButton}
            type="button"
            onClick={handleSubscribe}
            inProgress={subscribeInProgress}
            disabled={subscribeInProgress}
          >
            <FormattedMessage id="NewsletterPromptModal.subscribe" />
          </PrimaryButton>
          <InlineTextButton
            type="button"
            className={css.dismissButton}
            onClick={handleClose}
            disabled={subscribeInProgress}
          >
            <FormattedMessage id="NewsletterPromptModal.maybeLater" />
          </InlineTextButton>
        </div>
      </div>
    </Modal>
  );
};

NewsletterPromptModal.defaultProps = {
  id: 'NewsletterPromptModal',
};

NewsletterPromptModal.propTypes = {
  id: PropTypes.string,
};

export default NewsletterPromptModal;
